import React from "react";
import { useTranslation } from "react-i18next";
import "../assets/styles/TerraformArticle.scss";

function BackupArticle() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  if (isEn) {
    return (
      <div className="terraform-article">
        <h1>Backups, PRA & PRS: Why Testing Your Backups Saves You</h1>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
          Backup · Business Continuity · Disaster Recovery — April 2025
        </p>

        <h2>A Backup That Has Never Been Tested Is Not a Backup</h2>
        <p>
          Every organization has backups. But how many actually test them? The sobering reality:
          a backup that has never been restored is a false sense of security. The only way to know
          that a backup works is to restore it — under controlled conditions, before the crisis hits.
        </p>
        <p>
          Major incidents regularly reveal the same pattern: the backup existed, but it was corrupted,
          incomplete, or impossible to restore within the expected timeframe. The result is measured
          in lost hours, lost data, and sometimes lost business.
        </p>

        <h2>The 3-2-1 Rule: The Foundation of Any Backup Strategy</h2>
        <p>
          The 3-2-1 rule is the universally recognized starting point for a solid backup strategy:
        </p>
        <ul>
          <li><strong>3</strong> copies of the data (1 production + 2 backups)</li>
          <li><strong>2</strong> different storage media (local disk, NAS, tape, cloud...)</li>
          <li><strong>1</strong> copy offsite (geographically separated from the primary site)</li>
        </ul>
        <p>
          This rule protects against the most common scenarios: disk failure, ransomware (which
          encrypts local backups), fire or flooding at the primary site.
        </p>

        <h2>Backup Types: Full, Incremental, Differential</h2>
        <ul>
          <li>
            <strong>Full backup</strong> — complete copy of all data. Slow and storage-intensive,
            but fast to restore. Usually done weekly.
          </li>
          <li>
            <strong>Incremental backup</strong> — only changes since the last backup (full or incremental).
            Fast and light, but restoration requires chaining all incrementals since the last full.
          </li>
          <li>
            <strong>Differential backup</strong> — all changes since the last full backup. A compromise:
            restoration requires only the full + the last differential.
          </li>
        </ul>
        <pre><code>{`# Example Proxmox Backup Education (PBS)
# Full backup every Sunday, incremental every day
vzdump 100 --mode stop --compress zstd --storage pbs-backup
# Retention: keep 4 weekly, 7 daily, 2 monthly
prune --keep-weekly 4 --keep-daily 7 --keep-monthly 2`}</code></pre>

        <h2>RPO and RTO: The Two Critical Metrics</h2>
        <p>
          Any continuity plan revolves around two fundamental concepts:
        </p>
        <ul>
          <li>
            <strong>RPO (Recovery Point Objective)</strong> — the maximum acceptable data loss.
            If your RPO is 4 hours, you must back up at least every 4 hours. A 1-hour RPO requires
            near-real-time replication.
          </li>
          <li>
            <strong>RTO (Recovery Time Objective)</strong> — the maximum time to restore service.
            An RTO of 2 hours means the system must be operational within 2 hours of the incident.
          </li>
        </ul>
        <p>
          RPO and RTO are not technical choices — they are business decisions. A core CRM system
          will have an RPO of 1 hour and an RTO of 4 hours. An internal wiki can tolerate 24 hours
          for both. These values directly dictate the architecture and cost of the backup solution.
        </p>

        <h2>PRA: Business Recovery Plan</h2>
        <p>
          The <strong>PRA (Plan de Reprise d'Activité)</strong> — or DRP (Disaster Recovery Plan) —
          defines the procedures to restore critical IT systems after a major incident: datacenter
          outage, ransomware, physical disaster.
        </p>
        <p>
          A solid PRA includes:
        </p>
        <ul>
          <li>The inventory of critical systems and their priority order for recovery</li>
          <li>Defined RPO and RTO for each system</li>
          <li>Step-by-step restoration procedures, documented and version-controlled</li>
          <li>Identification of responsible persons (and their alternates)</li>
          <li>A list of contacts: suppliers, hosting providers, insurers</li>
          <li>The recovery infrastructure (failover site, cloud, spare hardware)</li>
        </ul>

        <h2>PRS: Service Recovery Plan</h2>
        <p>
          The <strong>PRS (Plan de Reprise de Service)</strong> is more granular than the PRA:
          it focuses on restoring a specific service (a database, an application, a network component)
          rather than the entire IS. It is the operational translation of the PRA for a given service.
        </p>
        <p>
          Each PRS details:
        </p>
        <ul>
          <li>The service dependencies (which infrastructure does it rely on?)</li>
          <li>The exact restoration steps in order</li>
          <li>The verification tests to validate that the service is functional</li>
          <li>Rollback procedures if restoration fails</li>
        </ul>

        <h2>Why You Must Test Your Backups</h2>
        <p>
          Testing is what distinguishes a real continuity plan from a theoretical document. Without
          regular testing, you don't know:
        </p>
        <ul>
          <li>Whether the backup is actually complete (some files may be locked at backup time)</li>
          <li>How long restoration actually takes (often much longer than estimated)</li>
          <li>Whether the restored data is consistent and usable</li>
          <li>Whether the team knows how to execute the procedures under stress</li>
        </ul>
        <pre><code>{`# Example automated restoration test (bash)
#!/bin/bash
BACKUP_DATE=$(date -d "yesterday" +%Y-%m-%d)
RESTORE_DIR="/tmp/restore-test-$BACKUP_DATE"

# Restore backup to an isolated directory
restic restore latest --target "$RESTORE_DIR"

# Verify critical files are present
if [ -f "$RESTORE_DIR/data/db.sql" ]; then
  echo "✓ Backup valid: db.sql present"
  # Test SQL integrity
  mysql --batch --execute "SELECT COUNT(*) FROM users" \
    < "$RESTORE_DIR/data/db.sql" > /dev/null 2>&1 \
    && echo "✓ SQL integrity OK" \
    || echo "✗ SQL integrity FAILED"
else
  echo "✗ Backup INVALID: db.sql missing"
  # Send alert
  curl -X POST "$SLACK_WEBHOOK" \
    -d '{"text":"⚠️ Backup test FAILED - '$BACKUP_DATE'"}'
fi`}</code></pre>

        <h2>Testing Frequency and Types</h2>
        <ul>
          <li>
            <strong>Daily automated test</strong> — verify that the backup was completed and that
            key files are present. Can be automated and monitored via Prometheus/Grafana.
          </li>
          <li>
            <strong>Monthly restoration test</strong> — restore to an isolated environment and
            verify service functionality. Measures actual RTO.
          </li>
          <li>
            <strong>Annual PRA/PRS exercise</strong> — full simulation of an incident scenario:
            the team follows the procedures as if a real crisis occurred. Identifies gaps and
            trains people.
          </li>
        </ul>

        <h2>Monitoring Backups</h2>
        <p>
          A backup that fails silently is a ticking time bomb. Every backup solution must be
          monitored:
        </p>
        <ul>
          <li>Alert when a backup job fails or does not start</li>
          <li>Monitor backup size (sudden decrease = sign of incomplete backup)</li>
          <li>Track restoration duration to verify RTO remains achievable</li>
          <li>Check expiry dates of certificates and access keys used by backup jobs</li>
        </ul>

        <h2>Conclusion</h2>
        <p>
          Backups are the safety net of any infrastructure. But a net that has never been tested
          can have holes you don't know about. The 3-2-1 rule, clearly defined RPO/RTO, documented
          PRA/PRS procedures, and — above all — regular testing: these are the four pillars of a
          genuine continuity strategy. In a context where ransomware and hardware failures are
          daily realities, treating backups as a strategic priority is not an option. It's
          what separates teams that recover in hours from those that recover in weeks.
        </p>
      </div>
    );
  }

  return (
    <div className="terraform-article">
      <h1>Sauvegardes, PRA & PRS : pourquoi tester ses backups vous sauve</h1>
      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "2rem" }}>
        Backup · Continuité d'activité · Reprise après sinistre — Avril 2025
      </p>

      <h2>Un backup qui n'a jamais été testé n'est pas un backup</h2>
      <p>
        Toute organisation a des sauvegardes. Mais combien les testent réellement ? La réalité est
        brutale : un backup qui n'a jamais été restauré est une fausse sécurité. La seule façon de
        savoir qu'une sauvegarde fonctionne, c'est de la restaurer — dans des conditions maîtrisées,
        avant que la crise n'arrive.
      </p>
      <p>
        Les incidents majeurs révèlent régulièrement le même schéma : la sauvegarde existait,
        mais elle était corrompue, incomplète, ou impossible à restaurer dans les délais attendus.
        Le résultat se mesure en heures perdues, en données perdues, et parfois en activité perdue.
      </p>

      <h2>La règle 3-2-1 : le socle de toute stratégie de sauvegarde</h2>
      <p>
        La règle 3-2-1 est le point de départ universellement reconnu pour une stratégie de
        sauvegarde solide :
      </p>
      <ul>
        <li><strong>3</strong> copies des données (1 production + 2 sauvegardes)</li>
        <li><strong>2</strong> supports de stockage différents (disque local, NAS, bande, cloud...)</li>
        <li><strong>1</strong> copie hors site (géographiquement séparée du site principal)</li>
      </ul>
      <p>
        Cette règle protège contre les scénarios les plus courants : défaillance disque, ransomware
        (qui chiffre les sauvegardes locales), incendie ou inondation sur le site principal.
      </p>

      <h2>Les types de sauvegarde : complète, incrémentielle, différentielle</h2>
      <ul>
        <li>
          <strong>Sauvegarde complète</strong> — copie intégrale de toutes les données. Lente et
          gourmande en espace, mais restauration rapide. Généralement hebdomadaire.
        </li>
        <li>
          <strong>Sauvegarde incrémentielle</strong> — seules les modifications depuis la dernière
          sauvegarde (complète ou incrémentielle). Rapide et légère, mais la restauration nécessite
          de chaîner toutes les incrémentales depuis la dernière complète.
        </li>
        <li>
          <strong>Sauvegarde différentielle</strong> — toutes les modifications depuis la dernière
          sauvegarde complète. Un compromis : la restauration ne nécessite que la complète + la
          dernière différentielle.
        </li>
      </ul>
      <pre><code>{`# Exemple de politique de sauvegarde Proxmox Backup Server (PBS)
# Sauvegarde complète chaque dimanche, incrémentielle chaque jour
vzdump 100 --mode stop --compress zstd --storage pbs-backup
# Rétention : conserver 4 hebdomadaires, 7 journalières, 2 mensuelles
prune --keep-weekly 4 --keep-daily 7 --keep-monthly 2`}</code></pre>

      <h2>RPO et RTO : les deux métriques critiques</h2>
      <p>
        Tout plan de continuité tourne autour de deux concepts fondamentaux :
      </p>
      <ul>
        <li>
          <strong>RPO (Recovery Point Objective)</strong> — la perte de données maximale acceptable.
          Si votre RPO est de 4 heures, vous devez sauvegarder au moins toutes les 4 heures. Un RPO
          d'1 heure nécessite une réplication quasi temps réel.
        </li>
        <li>
          <strong>RTO (Recovery Time Objective)</strong> — le délai maximum pour restaurer le service.
          Un RTO de 2 heures signifie que le système doit être opérationnel dans les 2 heures suivant
          l'incident.
        </li>
      </ul>
      <p>
        RPO et RTO ne sont pas des choix techniques — ce sont des décisions métier. Un CRM cœur
        d'activité aura un RPO d'1 heure et un RTO de 4 heures. Un wiki interne peut tolérer 24
        heures pour les deux. Ces valeurs dictent directement l'architecture et le coût de la
        solution de sauvegarde.
      </p>

      <h2>PRA : Plan de Reprise d'Activité</h2>
      <p>
        Le <strong>PRA</strong> définit les procédures pour restaurer les systèmes informatiques
        critiques après un incident majeur : panne du datacenter, ransomware, catastrophe physique.
      </p>
      <p>
        Un PRA solide comprend :
      </p>
      <ul>
        <li>L'inventaire des systèmes critiques et leur ordre de priorité de reprise</li>
        <li>Les RPO et RTO définis pour chaque système</li>
        <li>Les procédures de restauration pas à pas, documentées et versionnées</li>
        <li>L'identification des responsables (et leurs suppléants)</li>
        <li>La liste des contacts : fournisseurs, hébergeurs, assureurs</li>
        <li>L'infrastructure de reprise (site de secours, cloud, matériel de remplacement)</li>
      </ul>

      <h2>PRS : Plan de Reprise de Service</h2>
      <p>
        Le <strong>PRS</strong> est plus granulaire que le PRA : il se concentre sur la restauration
        d'un service spécifique (une base de données, une application, un composant réseau) plutôt
        que sur l'ensemble du SI. C'est la déclinaison opérationnelle du PRA pour un service donné.
      </p>
      <p>
        Chaque PRS détaille :
      </p>
      <ul>
        <li>Les dépendances du service (sur quelle infrastructure repose-t-il ?)</li>
        <li>Les étapes de restauration exactes dans l'ordre</li>
        <li>Les tests de vérification pour valider que le service est fonctionnel</li>
        <li>Les procédures de rollback si la restauration échoue</li>
      </ul>

      <h2>Pourquoi il faut tester ses sauvegardes</h2>
      <p>
        Les tests sont ce qui distingue un vrai plan de continuité d'un document théorique. Sans
        tests réguliers, vous ne savez pas :
      </p>
      <ul>
        <li>Si la sauvegarde est réellement complète (certains fichiers peuvent être verrouillés au moment de la sauvegarde)</li>
        <li>Combien de temps prend réellement la restauration (souvent bien plus long qu'estimé)</li>
        <li>Si les données restaurées sont cohérentes et exploitables</li>
        <li>Si l'équipe sait exécuter les procédures sous pression</li>
      </ul>
      <pre><code>{`# Exemple de test de restauration automatisé (bash)
#!/bin/bash
BACKUP_DATE=$(date -d "yesterday" +%Y-%m-%d)
RESTORE_DIR="/tmp/restore-test-$BACKUP_DATE"

# Restauration du backup dans un répertoire isolé
restic restore latest --target "$RESTORE_DIR"

# Vérification que les fichiers critiques sont présents
if [ -f "$RESTORE_DIR/data/db.sql" ]; then
  echo "✓ Backup valide : db.sql présent"
  # Test d'intégrité SQL
  mysql --batch --execute "SELECT COUNT(*) FROM users" \
    < "$RESTORE_DIR/data/db.sql" > /dev/null 2>&1 \
    && echo "✓ Intégrité SQL OK" \
    || echo "✗ Intégrité SQL ÉCHOUÉE"
else
  echo "✗ Backup INVALIDE : db.sql manquant"
  # Envoi d'une alerte
  curl -X POST "$SLACK_WEBHOOK" \
    -d '{"text":"⚠️ Test de backup ÉCHOUÉ - '$BACKUP_DATE'"}'
fi`}</code></pre>

      <h2>Fréquence et types de tests</h2>
      <ul>
        <li>
          <strong>Test automatisé quotidien</strong> — vérifier que la sauvegarde s'est bien
          terminée et que les fichiers clés sont présents. Peut être automatisé et monitoré
          via Prometheus/Grafana.
        </li>
        <li>
          <strong>Test de restauration mensuel</strong> — restaurer sur un environnement isolé
          et vérifier le bon fonctionnement du service. Mesure le RTO réel.
        </li>
        <li>
          <strong>Exercice PRA/PRS annuel</strong> — simulation complète d'un scénario d'incident :
          l'équipe suit les procédures comme si une vraie crise survenait. Permet d'identifier
          les lacunes et de former les équipes.
        </li>
      </ul>

      <h2>Superviser les sauvegardes</h2>
      <p>
        Une sauvegarde qui échoue silencieusement est une bombe à retardement. Toute solution
        de backup doit être monitorée :
      </p>
      <ul>
        <li>Alerte quand un job de sauvegarde échoue ou ne démarre pas</li>
        <li>Surveillance de la taille des sauvegardes (baisse soudaine = signe de sauvegarde incomplète)</li>
        <li>Suivi de la durée de restauration pour vérifier que le RTO reste atteignable</li>
        <li>Vérification des dates d'expiration des certificats et clés d'accès utilisés par les jobs de sauvegarde</li>
      </ul>

      <h2>Conclusion</h2>
      <p>
        Les sauvegardes sont le filet de sécurité de toute infrastructure. Mais un filet qui n'a
        jamais été testé peut avoir des trous qu'on ignore. La règle 3-2-1, des RPO/RTO clairement
        définis, des procédures PRA/PRS documentées, et — surtout — des tests réguliers : ce sont
        les quatre piliers d'une vraie stratégie de continuité. Dans un contexte où les ransomwares
        et les pannes matérielles sont des réalités quotidiennes, traiter les sauvegardes comme une
        priorité stratégique n'est pas une option. C'est ce qui distingue les équipes qui reprennent
        en heures de celles qui reprennent en semaines.
      </p>
    </div>
  );
}

export default BackupArticle;
